import React from "react";
import { toast } from "react-toastify";
import {
  Button,
  DynamicDropdown,
  DynamicMultiSelectDropdown,
  Form,
  TextInput,
} from "src/Components";
import { getUsers } from "src/Requests/connections";
import {
  getPortfolioGroupDropDown,
  previewUserAssign,
  setUserAssign,
  upsertGroupingSettingsUserAllowedCreditPercent,
} from "src/Requests/portfolioGroups";
import type { IUserAssignPreview } from "src/Interfaces/portfolioGroups";
import { packetErrorHandler } from "src/Utils/requestUtils";
import { getPercent } from "src/helper";
import AssignmentPreviewTables from "./AssignmentPreviewTables";

interface IProps {
  selectedRowsIds: number[];
  setSelectedRowsIds: (selectedRowsIds: number[]) => void;
  onClose: () => void;
  onSuccess: () => void;
}

const BatchEditAssignmentModal: React.FC<IProps> = ({
  selectedRowsIds,
  setSelectedRowsIds,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [preview, setPreview] = React.useState<IUserAssignPreview | null>(null);
  const [sahraUserIds, setSahraUserIds] =
    React.useState<number[]>(selectedRowsIds);
  const [portfolioGroupId, setPortfolioGroupId] = React.useState<
    number | string | undefined
  >();
  const [allowedCreditPercent, setAllowedCreditPercent] =
    React.useState<string>("");

  const clearPreview = () => {
    if (preview) setPreview(null);
  };

  const getValidValues = () => {
    if (!sahraUserIds.length) {
      toast.error("اکانت را انتخاب کنید");
      return null;
    }

    const groupId = Number(portfolioGroupId);
    if (Number.isNaN(groupId)) {
      toast.error("گروه پرتفو را انتخاب کنید");
      return null;
    }

    const hasAllowedCreditPercent = allowedCreditPercent !== "";
    const parsedAllowedCreditPercent = hasAllowedCreditPercent
      ? getPercent(allowedCreditPercent)
      : null;

    if (hasAllowedCreditPercent && parsedAllowedCreditPercent == null) {
      toast.error("درصد اعتبار مجاز را وارد کنید");
      return null;
    }

    return {
      sahraUserIds,
      groupId,
      parsedAllowedCreditPercent,
    };
  };

  const previewHandler = () => {
    const validValues = getValidValues();
    if (!validValues) return;

    setLoading(true);
    previewUserAssign({
      sahraUserIds: validValues.sahraUserIds,
      portfoGroupId: validValues.groupId,
    })
      .then((resp) => {
        if (resp.data) setPreview(resp.data);
        else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const submitHandler = () => {
    const validValues = getValidValues();
    if (!validValues) return;

    setLoading(true);
    setUserAssign({
      sahraUserIds: validValues.sahraUserIds,
      portfoGroupId: validValues.groupId,
    })
      .then(async (resp) => {
        if (!resp.data) {
          toast.error(resp.msg);
          return;
        }

        if (validValues.parsedAllowedCreditPercent != null) {
          const creditResponses = await Promise.all(
            validValues.sahraUserIds.map((sahraUserId) =>
              upsertGroupingSettingsUserAllowedCreditPercent({
                sahraUserId,
                value: Number(validValues.parsedAllowedCreditPercent),
              }),
            ),
          );
          const failedResponse = creditResponses.find((item) => !item.data);
          if (failedResponse) {
            toast.error(failedResponse.msg);
            return;
          }
        }

        toast.success(resp.msg || "با موفقیت ثبت شد");
        setSelectedRowsIds([]);
        onSuccess();
        onClose();
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <Form className={`p-4 gap-4 ${preview ? "!w-[560px]" : "!w-80"}`}>
      <div className="font-bold text-sm">ویرایش گروهی تخصیص اکانت</div>
      {!preview ? (
        <>
          <DynamicMultiSelectDropdown
            dropdown={{
              name: "sahraUserIds",
              placeholder: "اکانت",
              extraLabel: true,
            }}
            getData={getUsers}
            onChange={(_, value) => {
              clearPreview();
              setSahraUserIds(Array.isArray(value) ? value.map(Number) : []);
            }}
            value={sahraUserIds}
            withTagBox
          />
          <DynamicDropdown
            dropdown={{
              name: "portfolioGroupId",
              placeholder: "گروه پرتفو",
              extraLabel: true,
            }}
            getData={getPortfolioGroupDropDown}
            onChange={(_, value) => {
              clearPreview();
              setPortfolioGroupId(value);
            }}
            value={portfolioGroupId?.toString() ?? ""}
          />
          <TextInput
            label="درصد اعتبار مجاز"
            name="allowedCreditPercent"
            value={allowedCreditPercent}
            onChange={(_, value) => {
              clearPreview();
              setAllowedCreditPercent(value ?? "");
            }}
            kind="percent"
            extraLabel
          />
          <Button
            text="پیش نمایش"
            variant="contained"
            onClick={previewHandler}
            loading={loading}
          />
        </>
      ) : (
        <>
          <AssignmentPreviewTables preview={preview} />
          <div className="flex flex-row justify-end items-center gap-3">
            <Button
              text="بازگشت"
              variant="outlined"
              onClick={() => setPreview(null)}
              disabled={loading}
            />
            <Button
              text="ثبت"
              variant="contained"
              onClick={submitHandler}
              loading={loading}
            />
          </div>
        </>
      )}
    </Form>
  );
};

export default BatchEditAssignmentModal;
